import { computed, inject, Injectable, signal } from '@angular/core';
import {
  FamilyKidsService,
  type FamilyChild,
} from '../../parent-family/services/family-kids.service';
import { ParentDisclosureService } from './parent-disclosure.service';
import { ParentIdentityService } from './parent-identity.service';

@Injectable({ providedIn: 'root' })
export class IdentityNavService {
  #identity = inject(ParentIdentityService);
  #familyKids = inject(FamilyKidsService);
  #disclosure = inject(ParentDisclosureService);
  #loadPromise: Promise<void> | null = null;

  readonly ready = signal(false);

  readonly showNavLink = computed(
    () => this.ready() && this.#identity.layerEnabled(),
  );

  readonly unverifiedKids = this.#familyKids.unverifiedChildren;

  readonly attentionCount = computed(() => {
    if (!this.#identity.layerEnabled()) {
      return 0;
    }

    let count = 0;
    if (this.#identity.shouldPromptVerification()) {
      count += 1;
    } else if (this.#identity.inGracePeriod()) {
      count += 1;
    }
    count += this.unverifiedKids().length;
    count += this.#disclosure.pendingCount();
    return count;
  });

  readonly hasAttention = computed(() => this.attentionCount() > 0);

  async whenReady(): Promise<void> {
    if (this.ready() && this.#identity.configReady()) {
      return;
    }
    if (!this.#loadPromise) {
      this.#loadPromise = this.#load();
    }
    await this.#loadPromise;
  }

  async #load(): Promise<void> {
    try {
      await this.#identity.whenConfigReady();
      if (this.#identity.layerEnabled()) {
        // Nav visibility only needs config; status/disclosure can catch up after.
        void this.#identity.reload();
        void this.#disclosure.pendingQuery.refetch();
        void this.#disclosure.parentGrantsQuery.refetch();
      }
    } catch {
      // Keep shell usable even if identity bootstrap fails.
    } finally {
      this.ready.set(true);
      this.#loadPromise = null;
    }
  }

  kidDisplayName(child: FamilyChild): string {
    return (
      this.#familyKids.displayName(child) || child.email?.trim() || ''
    );
  }
}
